import React, { useId, useRef, useState } from 'react';
import {
  ArrowRight, Calculator, GraduationCap, Banknote, ClipboardCheck, Users,
  ShieldCheck, WifiOff, Printer, Zap,
} from 'lucide-react';
import { Link } from './SlashLink';
import { DecorLines } from './DecorLines';
import { TOOL_GROUPS } from '../data/navItems';
import { preloadForPath } from '../utils/lazyRoutes';

/* -------------------------------------------------------------------------- */
/* Data (every link is an existing page; paths use the trailing-slash form)    */
/* -------------------------------------------------------------------------- */

const CATEGORY_META: Record<string, { icon: typeof Calculator; blurb: string }> = {
  grades: {
    icon: Calculator,
    blurb: 'Sunday-night grade math, done in 30 seconds: weighted grades, the score you need on the final, curves, test scores and letter grades.',
  },
  gpa: {
    icon: GraduationCap,
    blurb: 'Stop guessing your GPA: GPA, weighted GPA, CGPA to percentage and everyday percentage maths, worked out instantly.',
  },
  everyday: {
    icon: Banknote,
    blurb: 'Everyday maths without the spreadsheet: tip, loan and mortgage calculators, plus a password generator that never leaves your device.',
  },
};

interface Persona {
  id: string;
  tab: string;
  icon: typeof Calculator;
  title: string;
  text: string;
  links: { label: string; path: string }[];
}

const PERSONAS: Persona[] = [
  {
    id: 'students',
    tab: 'Students',
    icon: GraduationCap,
    title: 'Know where you stand before the final',
    text: 'Work out your course grade, see exactly what you need on the final exam and keep your GPA on track.',
    links: [
      { label: 'Weighted grade calculator', path: '/grade-calculator/' },
      { label: 'Final exam grade calculator', path: '/final-exam-grade-calculator/' },
      { label: 'GPA calculator', path: '/gpa-calculator/' },
    ],
  },
  {
    id: 'teachers',
    tab: 'Teachers',
    icon: ClipboardCheck,
    title: 'Grade a stack of tests in minutes',
    text: 'Look up the percentage and letter grade for every number of wrong answers, then print the chart or save it as a PDF.',
    links: [
      { label: 'Quick Grade chart', path: '/' },
      { label: 'Grading scale charts', path: '/grading-scale/' },
      { label: 'Grade curve calculator', path: '/grade-curve-calculator/' },
      { label: 'How many questions can I miss?', path: '/how-many-questions-can-i-miss/' },
    ],
  },
  {
    id: 'everyone',
    tab: 'Everyone',
    icon: Users,
    title: 'Everyday maths without the spreadsheet',
    text: 'Percentages, tips, loans and mortgages: type the numbers and get a clear answer straight away.',
    links: [
      { label: 'Percentage calculator', path: '/percentage-calculator/' },
      { label: 'Tip calculator', path: '/tip-calculator/' },
      { label: 'Loan calculator', path: '/loan-calculator/' },
    ],
  },
];

const TRUST: { icon: typeof Calculator; title: string; text: string }[] = [
  { icon: Zap, title: 'Free, no sign-up', text: 'Open a calculator and start. There is no account, paywall or trial.' },
  { icon: ShieldCheck, title: 'Private by design', text: 'Calculations run in your browser. Nothing you type into a calculator is uploaded.' },
  { icon: WifiOff, title: 'Works offline', text: 'Install it once and your calculators keep working without a connection.' },
  { icon: Printer, title: 'Print or save as PDF', text: 'Take a clean chart or grade report to class, or keep a copy for your records.' },
];

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Landing sections shown under the Quick Grade tool on the home page (Coursera-style):
 *   1. "Explore calculators" tabbed shelves  2. "What brings you here today?" tabs   3. "Why people choose" tiles
 * Server-render safe (no window access while rendering). Headings start at h2: the page's single h1 lives in <ToolHeading />.
 * onOpenCommandPalette is wired from App through GradeCalculator; when absent the
 * escape hatch falls back to an anchor link to the shelves.
 */
export const HomeSections: React.FC<{ onOpenCommandPalette?: () => void }> = ({ onOpenCommandPalette }) => {
  const baseId = useId();
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const persona = PERSONAS[active];
  const PersonaIcon = persona.icon;
  const [shelfActive, setShelfActive] = useState(0);
  const shelfRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onTabKey = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % PERSONAS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + PERSONAS.length) % PERSONAS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = PERSONAS.length - 1;
    else return;
    e.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const onShelfKey = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (e.key === 'ArrowRight') next = (index + 1) % TOOL_GROUPS.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + TOOL_GROUPS.length) % TOOL_GROUPS.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = TOOL_GROUPS.length - 1;
    else return;
    e.preventDefault();
    setShelfActive(next);
    shelfRefs.current[next]?.focus();
  };

  return (
    <div className="mt-10 print:hidden">
      {/* Outcome ticker: verified results and facts, scrolling under the hero */}
      <div className="nova-ticker" aria-label="Example results">
        <div className="nova-ticker-track">
          <span>17 out of 20 = 85% — a B&nbsp;&nbsp;•&nbsp;&nbsp;3.60 semester GPA, worked example&nbsp;&nbsp;•&nbsp;&nbsp;0 sign-ups&nbsp;&nbsp;•&nbsp;&nbsp;0 student data stored&nbsp;&nbsp;•&nbsp;&nbsp;Print or save as PDF&nbsp;&nbsp;•&nbsp;&nbsp;76 free pages&nbsp;&nbsp;•&nbsp;&nbsp;</span>
          <span aria-hidden="true">17 out of 20 = 85% — a B&nbsp;&nbsp;•&nbsp;&nbsp;3.60 semester GPA, worked example&nbsp;&nbsp;•&nbsp;&nbsp;0 sign-ups&nbsp;&nbsp;•&nbsp;&nbsp;0 student data stored&nbsp;&nbsp;•&nbsp;&nbsp;Print or save as PDF&nbsp;&nbsp;•&nbsp;&nbsp;76 free pages&nbsp;&nbsp;•&nbsp;&nbsp;</span>
        </div>
      </div>

      {/* 0. Quick answers: the lookups people search for most */}
      <section aria-labelledby={`${baseId}-quick-answers`} className="cs-section pt-4">
        <p className="cs-eyebrow">Quick answers</p>
        <h2 id={`${baseId}-quick-answers`} className="cs-h2">Grade questions, answered instantly</h2>
        <DecorLines variant="squiggle" className="cs-squiggle" />
        <div className="grid sm:grid-cols-2 gap-3 max-w-4xl">
          {[
            { q: 'What grade is 17 out of 20?', a: '85% — a B on the standard scale.', path: '/test-grade-calculator/' },
            { q: 'What grade is 44 out of 60?', a: '73.3% — a C on the standard scale.', path: '/test-grade-calculator/' },
            { q: 'What grade is 9 out of 10?', a: '90% — an A on the standard scale.', path: '/grading-scale/10-questions/' },
            { q: 'What grade is 23 out of 25?', a: '92% — an A on the standard scale.', path: '/grading-scale/25-questions/' },
          ].map((item) => (
            <Link
              key={item.q}
              to={item.path}
              className="block rounded-2xl border border-slate-200 bg-white p-4 no-underline hover:border-teal-600 transition-colors"
            >
              <span className="block font-bold text-slate-900 ">{item.q}</span>
              <span className="block text-sm text-slate-600 ">{item.a} Check any other score →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* 1. From scores to a plan: the grading journey, as linked steps */}
      <section aria-labelledby={`${baseId}-flow`} className="cs-section pt-4">
        <p className="cs-eyebrow">How it flows</p>
        <h2 id={`${baseId}-flow`} className="cs-h2">From scores to a plan, in four steps</h2>
        <DecorLines variant="squiggle" className="cs-squiggle" />
        <ol className="nova-flow">
          <li><Link to="/test-grade-calculator/"><strong>Grade a test</strong><span>Percentage and letter grade for any score.</span></Link></li>
          <li><Link to="/grading-scale/"><strong>Check the scale</strong><span>Every wrong-answer row, at a glance.</span></Link></li>
          <li><Link to="/gpa-calculator/"><strong>Track the GPA</strong><span>Semester, cumulative, weighted — all in one place.</span></Link></li>
          <li><Link to="/final-grade-planner/"><strong>Plan the finish</strong><span>What each class needs to hit your target.</span></Link></li>
        </ol>
      </section>

      {/* 2. Explore calculators — tabbed shelves (Typeform shelves + Asana switcher).
          Every panel is rendered and only hidden, so no homepage link is lost from
          the prerendered HTML; tabs only switch which shelf is visible. */}
      <section aria-labelledby={`${baseId}-explore`} className="cs-section pt-4" id="nova2-explore">
        <p className="cs-eyebrow">Explore</p>
        <h2 id={`${baseId}-explore`} className="cs-h2">Calculators for every kind of grade and number</h2>
        <DecorLines variant="squiggle" className="cs-squiggle" />
        <p className="cs-lead">Pick a shelf, or press Ctrl+K to search every tool.</p>
        <div role="tablist" aria-label="Calculator categories" className="nova2-shelf-tabs">
          {TOOL_GROUPS.map((group, i) => {
            const meta = CATEGORY_META[group.id] ?? { icon: Calculator, blurb: '' };
            const Icon = meta.icon;
            return (
              <button
                key={group.id}
                ref={(el) => { shelfRefs.current[i] = el; }}
                type="button"
                role="tab"
                id={`${baseId}-shelf-tab-${group.id}`}
                aria-selected={shelfActive === i}
                aria-controls={`${baseId}-shelf-panel-${group.id}`}
                tabIndex={shelfActive === i ? 0 : -1}
                onClick={() => setShelfActive(i)}
                onKeyDown={(e) => onShelfKey(e, i)}
                className="nova2-shelf-tab"
              >
                <Icon className="size-5" strokeWidth={2} aria-hidden="true" />
                {group.heading}
              </button>
            );
          })}
        </div>
        {TOOL_GROUPS.map((group, i) => {
          const meta = CATEGORY_META[group.id] ?? { icon: Calculator, blurb: '' };
          return (
            <div
              key={group.id}
              role="tabpanel"
              id={`${baseId}-shelf-panel-${group.id}`}
              aria-labelledby={`${baseId}-shelf-tab-${group.id}`}
              hidden={shelfActive !== i}
              className="nova2-shelf-panel"
            >
              <p className="cs-lead">{meta.blurb}</p>
              <ul className="nova2-shelf-list">
                {group.items.map((item) => (
                  <li key={item.path}>
                    <Link to={item.path} title={item.title} onMouseEnter={() => void preloadForPath(item.path)} onFocus={() => void preloadForPath(item.path)}>
                      <span>{item.label}</span>
                      <ArrowRight className="size-4 shrink-0 opacity-60" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
              {group.items[0] && (
                <Link to={group.items[0].path} className="cs-card-link">
                  Start with {group.items[0].label} <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              )}
            </div>
          );
        })}
      </section>

      {/* 3. What's new: genuinely new tools, dated, all linking to real pages */}
      <section aria-labelledby={`${baseId}-new`} className="cs-section">
        <p className="cs-eyebrow">What&apos;s new</p>
        <h2 id={`${baseId}-new`} className="cs-h2">New tools, added October 2026</h2>
        <DecorLines variant="squiggle" className="cs-squiggle" />
        <ul className="nova-new">
          <li>
            <Link to="/final-grade-planner/"><strong>Final-grade planner</strong><span>Plan up to 10 classes — and see impossible targets flagged honestly.</span></Link>
          </li>
          <li>
            <Link to="/high-school-gpa-calculator/"><strong>High-school &amp; college GPA calculators</strong><span>Built for each system&apos;s own rules, with worked examples.</span></Link>
          </li>
          <li>
            <Link to="/cgpa-to-percentage-calculator/"><strong>Per-university CGPA converters</strong><span>25 verified university formulas, no invented conversions.</span></Link>
          </li>
        </ul>
      </section>

      {/* 4. What brings you here today? */}
      <section aria-labelledby={`${baseId}-who`} className="cs-section">
        <h2 id={`${baseId}-who`} className="cs-h2">What brings you here today?</h2>
        <DecorLines variant="squiggle" className="cs-squiggle" />
        <p className="cs-lead">Choose what describes you and jump straight to the tools people like you use most.</p>
        <div role="tablist" aria-label="Who are you?" className="cs-tabs">
          {PERSONAS.map((p, i) => (
            <button
              key={p.id}
              ref={(el) => { tabRefs.current[i] = el; }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${p.id}`}
              aria-selected={active === i}
              aria-controls={`${baseId}-panel`}
              tabIndex={active === i ? 0 : -1}
              onClick={() => setActive(i)}
              onKeyDown={(e) => onTabKey(e, i)}
              className="cs-tab"
            >
              {p.tab}
            </button>
          ))}
        </div>
        <div role="tabpanel" id={`${baseId}-panel`} aria-labelledby={`${baseId}-tab-${persona.id}`} className="cs-panel">
          <DecorLines variant="arcs" className="cs-panel-arcs" />
          <div>
            <span className="cs-cat-icon" aria-hidden="true"><PersonaIcon className="size-6" strokeWidth={2} /></span>
            <h3>{persona.title}</h3>
            <p>{persona.text}</p>
            <Link to={persona.links[0].path} className="cs-btn cs-btn-primary">
              {persona.links[0].label}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
          <ul className="cs-panel-links">
            {persona.links.map((l) => (
              <li key={l.path}>
                <Link to={l.path} onMouseEnter={() => void preloadForPath(l.path)}>
                  <span>{l.label}</span>
                  <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 5. Why grading eats your Sunday — pain, agitation, solution (Asana).
          No statistics: every pain is a plain, verifiable frustration, and the
          fix line states only what the tools actually do. */}
      <section aria-labelledby={`${baseId}-pain`} className="cs-section">
        <p className="cs-eyebrow">Why grading eats your Sunday</p>
        <h2 id={`${baseId}-pain`} className="cs-h2">Sound familiar?</h2>
        <DecorLines variant="squiggle" className="cs-squiggle" />
        <ul className="nova2-pain-grid">
          <li className="nova2-pain-card">
            <h3>Grades live in five places</h3>
            <p>One spreadsheet, one gradebook, one stack of papers — and none of them agree with each other.</p>
          </li>
          <li className="nova2-pain-card">
            <h3>One wrong weight ruins the average</h3>
            <p>A final worth 30% typed as 3% quietly changes every student&apos;s grade, and nobody notices until report cards.</p>
          </li>
          <li className="nova2-pain-card">
            <h3>“What do I need on the final?” — again</h3>
            <p>Every student asks it, and every answer means recomputing the whole course grade by hand.</p>
          </li>
        </ul>
        <div className="nova2-pain-fix">
          <p><strong>This is exactly what Easy Grade Tool is built to fix:</strong> type the numbers once, get the answer instantly — no sign-up, no spreadsheet, no student data stored.</p>
          <Link to="/grade-calculator/" className="cs-btn cs-btn-primary">
            Start with the weighted grade calculator
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* 6. Why people choose Easy Grade Tool */}
      <section aria-labelledby={`${baseId}-why`} className="cs-section pb-2">
        <h2 id={`${baseId}-why`} className="cs-h2">Why people choose Easy Grade Tool</h2>
        <DecorLines variant="squiggle" className="cs-squiggle" />
        <p className="cs-lead">Simple, accurate and respectful of your data.</p>
        <div className="cs-trust">
          {TRUST.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className="cs-trust-item">
                <Icon className="size-7" strokeWidth={2} aria-hidden="true" />
                <h3>{t.title}</h3>
                <p>{t.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* 7. Escape hatch (Typeform): for visitors who fit no shelf, search wins. */}
      <section aria-labelledby={`${baseId}-escape`} className="cs-section pb-2">
        <div className="nova2-escape">
          <h2 id={`${baseId}-escape`} className="cs-h2">Looking for a specific calculator?</h2>
          <p>Search every tool by name — faster than browsing the shelves.</p>
          {onOpenCommandPalette ? (
            <button type="button" onClick={onOpenCommandPalette} className="cs-btn cs-btn-primary">
              Search calculators (Ctrl+K)
            </button>
          ) : (
            <a href="#nova2-explore" className="cs-btn cs-btn-primary">
              Browse all calculators
            </a>
          )}
        </div>
      </section>
    </div>
  );
};

export default HomeSections;
